/**
 * Real Lottery System JavaScript
 * Handles the new real lottery flow where prizes are pre-assigned to ticket positions
 */

// ========================================
// TESTING MODE - SET TO true TO DISABLE API CALLS
// Remember to set back to false when done testing!
// ========================================
const DISABLE_LOTTERY_API_CALLS = false;

const RealLottery = {
  // API Base URL
  API_BASE: "https://home-5017198992.app-ionos.space/api",

  // State
  lotteryType: null,
  lotteryId: null,
  productId: null,
  orderId: null,
  customerId: null,
  tickets: [],
  availableTickets: [],
  claimedTickets: [],
  selectedTicketIds: [], // Store selected ticket IDs for reveal
  remainingToClaim: 0,
  totalToClaim: 0,

  // Reservation state
  reservedTicketIds: [],
  hasExistingReservation: false,

  /**
   * Initialize real lottery system
   * @param {string} productId - Shopify product ID
   * @param {string} customerId - Customer ID (order ID)
   * @param {function} onReservationCheck - Optional callback when reservation check completes
   */
  init(productId, customerId, onReservationCheck) {
    console.log(
      "RealLottery.init called with productId:",
      productId,
      "customerId:",
      customerId
    );
    this.productId = productId;
    this.orderId = customerId;
    this.customerId = customerId;

    // Load tickets asynchronously
    this.loadLotteryTickets();
    this.loadUserClaimedTickets();

    // Check for existing reservations
    const self = this;
    this.checkExistingReservation(function (response) {
      console.log("Reservation check on init:", response);
      if (onReservationCheck) {
        onReservationCheck(response);
      }
    });
  },

  /**
   * Get lottery type (real or fake) - called via JSONP
   */
  getLotteryType(callback) {
    const callbackName = "lotteryTypeCallback_" + Date.now();
    $.getJSON(
      `${this.API_BASE}/lottery-type-jsonp/${this.productId}?callback=?`,
      callback
    );
  },

  /**
   * Load all tickets for the lottery
   * @param {function} callback - Optional callback when tickets are loaded
   */
  loadLotteryTickets(callback) {
    const self = this;
    $.getJSON(
      `${this.API_BASE}/lottery-tickets-jsonp/${this.productId}?order_id=${encodeURIComponent(
        this.orderId || ""
      )}&user_id=${encodeURIComponent(this.customerId || "")}&callback=?`,
      function (response) {
        console.log("Tickets loaded:", response);
        if (response.status && response.lottery_type === "real") {
          self.tickets = response.tickets || [];
          self.availableTickets = self.tickets.filter(
            (t) =>
              t.status === "available" &&
              t.prize_label !== "LAST" &&
              !t.is_reserved_by_other
          );
          console.log("Available tickets:", self.availableTickets.length);
          self.syncClaimableUi();
        }
        if (callback) callback(response);
      }
    );
  },

  /**
   * Load user's claimed tickets for this order
   */
  loadUserClaimedTickets() {
    const self = this;
    $.getJSON(
      `${this.API_BASE}/user-claimed-tickets-jsonp/${this.orderId}/${this.productId}?callback=?`,
      function (response) {
        console.log("User claimed tickets loaded:", response);
        if (response.status && response.lottery_type === "real") {
          self.claimedTickets = response.claimed_tickets || [];
          self.totalToClaim = response.total_to_claim || 0;
          self.remainingToClaim = response.remaining_to_claim || 0;
          console.log("Remaining to claim:", self.remainingToClaim);
          self.syncClaimableUi();
        }
      }
    );
  },

  getRequestedClaimableCount() {
    const inputValue = parseInt(
      document.getElementById("avaliabletc01")?.value || 0,
      10
    );

    if (Number.isFinite(this.remainingToClaim) && this.remainingToClaim > 0) {
      return this.remainingToClaim;
    }

    return Number.isFinite(inputValue) ? inputValue : 0;
  },

  getEffectiveClaimableCount() {
    const requestedCount = this.getRequestedClaimableCount();

    if (!Array.isArray(this.tickets) || this.tickets.length === 0) {
      return requestedCount;
    }

    const selectableCount = this.availableTickets.length;
    return Math.min(requestedCount, selectableCount);
  },

  syncClaimableUi() {
    const effectiveCount = this.getEffectiveClaimableCount();

    document.querySelectorAll(".avaliabletc").forEach((element) => {
      element.innerText = effectiveCount;
    });

    const availableInput = document.getElementById("avaliabletc01");
    if (availableInput) {
      availableInput.value = effectiveCount;
    }

    const newQtyInput = document.getElementById("newqty");
    if (newQtyInput) {
      newQtyInput.value = effectiveCount;
    }

    const ad2 = document.getElementById("ad2");
    if (ad2) {
      ad2.innerText = effectiveCount;
    }
  },

  /**
   * Claim a specific ticket
   * @param {number} ticketId - ID of the ticket to claim
   * @param {function} callback - Callback function
   */
  claimTicket(ticketId, callback) {
    // TESTING MODE: Skip actual API call but return mock ticket data
    if (DISABLE_LOTTERY_API_CALLS) {
      console.log("⚠️ TESTING MODE: claimTicket skipped for ticket:", ticketId);

      // Find the ticket in our loaded tickets to get prize info
      const ticket = this.tickets.find(
        (t) => t.id == ticketId || t.ticket_id == ticketId
      );
      let mockTicket = {
        id: ticketId,
        ticket_number: ticket
          ? ticket.ticket_number
          : Math.floor(Math.random() * 100),
        prize_label: ticket ? ticket.prize_label || ticket.lable || "A" : "A",
        prize_title: ticket
          ? ticket.prize_title || ticket.f_title || "Test Prize"
          : "Test Prize",
        prize_image: ticket
          ? ticket.prize_image || ticket.f_image || null
          : null,
      };

      console.log("⚠️ TESTING MODE: Mock ticket data:", mockTicket);

      if (callback)
        callback({
          status: true,
          remaining_to_claim: this.remainingToClaim - 1,
          ticket: mockTicket,
        });
      return;
    }

    const self = this;
    const url = `${this.API_BASE}/claim-ticket-jsonp/${ticketId}?order_id=${
      this.orderId
    }&customer_id=${encodeURIComponent(this.customerId)}&callback=?`;

    $.getJSON(url, function (response) {
      console.log("Claim response:", response);
      if (response.status) {
        self.remainingToClaim = response.remaining_to_claim;
      }
      if (callback) callback(response);
    });
  },

  /**
   * Generate ticket grid HTML for Step 2
   * @param {HTMLElement} container - Container element for the grid
   */
  renderTicketGrid(container) {
    console.log("renderTicketGrid called, tickets:", this.tickets.length);
    if (!container) return;

    // If tickets not loaded yet, show loading and reload
    if (this.tickets.length === 0) {
      container.innerHTML =
        '<div class="loading-tickets">Loading tickets...</div>';
      const self = this;
      this.loadLotteryTickets(function () {
        self.renderTicketGridContent(container);
        // After rendering, pre-select any reserved tickets
        setTimeout(function () {
          self.preselectReservedTickets();
        }, 100);
      });
      return;
    }

    this.renderTicketGridContent(container);
    // After rendering, pre-select any reserved tickets
    const self = this;
    setTimeout(function () {
      self.preselectReservedTickets();
    }, 100);
  },

  /**
   * Actually render the ticket grid content
   * @param {HTMLElement} container - Container element for the grid
   */
  renderTicketGridContent(container) {
    container.innerHTML = "";

    // Remove the real-lottery-grid class - use original styling
    container.classList.remove("real-lottery-grid");

    // Get ticket image URL from container data attributes (set by Liquid)
    const ticketImgUrl = container.getAttribute("data-ticket-img") || "";
    // Use outer frame for sold tickets (transparent center to show prize)
    const outerFrameUrl =
      container.getAttribute("data-outer-frame") || ticketImgUrl;

    this.tickets.forEach((ticket, index) => {
      // Skip LAST prize tickets in the selectable grid
      if (ticket.prize_label === "LAST") return;

      const ticketDiv = document.createElement("div");
      ticketDiv.classList.add("ticketAVL");
      ticketDiv.dataset.ticketId = ticket.id;
      ticketDiv.dataset.ticketNumber = ticket.ticket_number;

      if (ticket.status === "sold") {
        // Sold ticket - show with revealed prize in style matching pull ribbon reveal
        ticketDiv.classList.add("soldTicket");
        const labelLetter = ticket.prize_label
          ? ticket.prize_label.replace("賞", "")
          : "?";
        ticketDiv.innerHTML = `
                    <label class="ticketContainer soldContainer">
                        <div class="sold-prize-reveal">
                            <div class="sold-prize-badge">${labelLetter}</div>
                        </div>
                        <img src="${outerFrameUrl}" alt="Sold" class="sold-outer-frame">
                    </label>
                `;
      } else if (ticket.is_reserved_by_other) {
        // Reserved by another user - show as unavailable
        ticketDiv.classList.add("reservedByOther");
        ticketDiv.innerHTML = `
                    <label class="ticketContainer reservedContainer">
                        <div class="reserved-overlay">
                            <span class="reserved-icon">🔒</span>
                        </div>
                        <img src="${ticketImgUrl}" alt="Reserved" style="opacity: 0.5;">
                    </label>
                `;
      } else {
        // Available ticket - MATCH ORIGINAL DESIGN EXACTLY
        ticketDiv.classList.add("fpooi");
        ticketDiv.innerHTML = `
                    <label class="ticketContainer" for="realTicket${ticket.id}">
                        <input type="checkbox" id="realTicket${ticket.id}" class="ticketBox realTicketCheckbox" data-ticket-id="${ticket.id}" onchange="RealLottery.handleTicketSelection(this)">
                        <span class="ticketCheckmark"></span>
                        <img src="${ticketImgUrl}" alt="img">
                    </label>
                `;
      }

      container.appendChild(ticketDiv);
    });

    console.log("Ticket grid rendered");
  },

  /**
   * Handle ticket selection change
   */
  handleTicketSelection(checkbox) {
    const ticketDiv = checkbox.closest(".ticketAVL");
    const allSelected = document.querySelectorAll(
      ".realTicketCheckbox:checked"
    );
    const selectedCount = allSelected.length;
    const maxSelection = this.getEffectiveClaimableCount();

    console.log("Selection changed:", selectedCount, "/", maxSelection);

    if (checkbox.checked) {
      if (selectedCount > maxSelection) {
        // Silently prevent over-selection (no alert)
        checkbox.checked = false;
        return;
      }
      ticketDiv.classList.add("selectedTicket");
    } else {
      ticketDiv.classList.remove("selectedTicket");
    }

    // Update button state
    const selectBtn = document.getElementById("selectbtn");
    const ad1 = document.getElementById("ad1");
    const ad2 = document.getElementById("ad2");

    if (ad1) ad1.innerText = selectedCount;
    if (ad2) ad2.innerText = maxSelection;

    // Get all real lottery checkboxes
    const allCheckboxes = document.querySelectorAll(".realTicketCheckbox");

    if (selectBtn) {
      if (selectedCount === maxSelection) {
        // Enable button and add enableBtn class
        selectBtn.disabled = false;
        selectBtn.classList.add("enableBtn");
        // Disable unchecked checkboxes to prevent further selection
        allCheckboxes.forEach((cb) => {
          if (!cb.checked) {
            cb.disabled = true;
          }
        });
      } else {
        // Disable button and remove enableBtn class
        selectBtn.disabled = true;
        selectBtn.classList.remove("enableBtn");
        // Re-enable all checkboxes
        allCheckboxes.forEach((cb) => {
          cb.disabled = false;
        });
      }
    }
  },

  /**
   * Set up ticket selection handlers
   * @param {number} maxSelection - Maximum tickets user can select
   */
  setupTicketSelection(maxSelection) {
    this.remainingToClaim = maxSelection;
    const effectiveCount = this.getEffectiveClaimableCount();
    console.log("setupTicketSelection, max:", maxSelection, "effective:", effectiveCount);

    // Update counter
    const ad2 = document.getElementById("ad2");
    if (ad2) ad2.innerText = effectiveCount;

    this.syncClaimableUi();
  },

  /**
   * Get selected ticket IDs
   */
  getSelectedTicketIds() {
    const selected = document.querySelectorAll(".realTicketCheckbox:checked");
    const ids = Array.from(selected).map((cb) => cb.dataset.ticketId);
    this.selectedTicketIds = ids;
    console.log("Selected ticket IDs:", ids);
    return ids;
  },

  /**
   * Process ticket reveals - claim tickets and prepare data for reveal modal
   * @param {array} ticketIds - Array of ticket IDs to claim
   * @param {function} callback - Callback with claimed ticket data
   */
  processTicketReveals(ticketIds, callback) {
    const self = this;
    const claimedTickets = [];
    let processed = 0;

    console.log("Processing reveals for tickets:", ticketIds);

    if (ticketIds.length === 0) {
      callback([]);
      return;
    }

    // Claim each ticket sequentially
    function claimNext(index) {
      if (index >= ticketIds.length) {
        // All done
        console.log("All tickets claimed:", claimedTickets);
        callback(claimedTickets);
        return;
      }

      self.claimTicket(ticketIds[index], function (response) {
        if (response.status && response.ticket) {
          claimedTickets.push({
            id: response.ticket.id,
            ticket_number: response.ticket.ticket_number,
            prize_label: response.ticket.prize_label,
            prize_title: response.ticket.prize_title,
            prize_image: response.ticket.prize_image,
            is_special: response.ticket.is_special,
          });

          // Check for LAST prize auto-award
          if (response.last_prize_awarded && response.last_prize) {
            claimedTickets.push({
              id: response.last_prize.id,
              ticket_number: "LAST",
              prize_label: "LAST",
              prize_title: response.last_prize.prize_title,
              prize_image: response.last_prize.prize_image,
              is_special: response.last_prize.is_special || true, // LAST is always special
            });
          }
        }

        // Claim next ticket
        claimNext(index + 1);
      });
    }

    claimNext(0);
  },

  /**
   * Check if all user's tickets have been claimed
   */
  isComplete() {
    return this.remainingToClaim === 0;
  },

  /**
   * Get prize image URL
   */
  getPrizeImageUrl(ticket) {
    if (!ticket.prize_image) return null;

    const img = ticket.prize_image;
    if (/^https?:\/\//i.test(img)) return img;

    const baseUrl =
      "https://home-5017198992.app-ionos.space/storage/app/public";
    if (ticket.prize_label === "LAST") {
      return `${baseUrl}/lottery_last/${img}`;
    }
    return `${baseUrl}/lottery_first/${img}`;
  },

  // ==========================================
  // RESERVATION SYSTEM METHODS
  // ==========================================

  /**
   * Check for existing reservations on page load
   * @param {function} callback - Callback with reservation status
   */
  checkExistingReservation(callback) {
    const self = this;
    const url = `${this.API_BASE}/check-reservation-jsonp/${this.productId}?order_id=${this.orderId}&callback=?`;

    console.log("Checking for existing reservation...");
    $.getJSON(url, function (response) {
      console.log("Reservation check response:", response);
      if (response.status && response.has_reservation) {
        self.hasExistingReservation = true;
        self.reservedTicketIds = response.reserved_ticket_ids || [];
        console.log(
          "Has existing reservation, tickets:",
          self.reservedTicketIds
        );
      } else {
        self.hasExistingReservation = false;
        self.reservedTicketIds = [];
      }
      if (callback) callback(response);
    });
  },

  /**
   * Reserve selected tickets
   * @param {array} ticketIds - Array of ticket IDs to reserve
   * @param {function} callback - Callback with reservation result
   */
  reserveTickets(ticketIds, callback) {
    // TESTING MODE: Skip actual API call
    if (DISABLE_LOTTERY_API_CALLS) {
      console.log(
        "⚠️ TESTING MODE: reserveTickets skipped for tickets:",
        ticketIds
      );
      this.reservedTicketIds = ticketIds;
      this.hasExistingReservation = true;
      if (callback) callback({ status: true });
      return;
    }

    const self = this;
    const ticketIdsParam = ticketIds.join(",");
    const url = `${this.API_BASE}/reserve-tickets-jsonp/${this.productId}?order_id=${this.orderId}&user_id=${this.customerId}&ticket_ids=${ticketIdsParam}&callback=?`;

    console.log("Reserving tickets:", ticketIds);
    $.getJSON(url, function (response) {
      console.log("Reserve response:", response);
      if (response.status) {
        self.reservedTicketIds = ticketIds;
        self.hasExistingReservation = true;
      }
      if (callback) callback(response);
    });
  },

  /**
   * Clear current reservations (when going back to Step 2)
   * @param {function} callback - Callback when done
   */
  clearReservation(callback) {
    const self = this;
    const url = `${this.API_BASE}/clear-reservation-jsonp/${this.productId}?order_id=${this.orderId}&user_id=${this.customerId}&callback=?`;

    console.log("Clearing reservation...");
    $.getJSON(url, function (response) {
      console.log("Clear reservation response:", response);
      if (response.status) {
        self.reservedTicketIds = [];
        self.hasExistingReservation = false;
      }
      if (callback) callback(response);
    });
  },

  /**
   * Show conflict warning popup
   * @param {array} conflictingIds - IDs of tickets that were taken by others
   */
  showConflictWarning(conflictingIds) {
    const conflictCount = conflictingIds.length;
    const message =
      conflictCount === 1
        ? "Ein von Ihnen ausgewähltes Ticket wurde gerade von einem anderen Benutzer reserviert. Bitte aktualisieren Sie die Seite und wählen Sie neue Tickets aus."
        : `${conflictCount} von Ihnen ausgewählte Tickets wurden gerade von anderen Benutzern reserviert. Bitte aktualisieren Sie die Seite und wählen Sie neue Tickets aus.`;

    // Create or show conflict modal
    let modal = document.getElementById("reservationConflictModal");
    if (!modal) {
      modal = document.createElement("div");
      modal.id = "reservationConflictModal";
      modal.innerHTML = `
        <div class="conflict-modal-backdrop" onclick="RealLottery.hideConflictWarning()"></div>
        <div class="conflict-modal-content">
          <div class="conflict-icon">⚠️</div>
          <h3>Tickets nicht mehr verfügbar</h3>
          <p id="conflictMessage">${message}</p>
          <button onclick="location.reload()" class="conflict-reload-btn">Seite aktualisieren</button>
        </div>
      `;
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 99999;
        display: none;
      `;
      document.body.appendChild(modal);

      // Add styles
      const style = document.createElement("style");
      style.textContent = `
        .conflict-modal-backdrop {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.7);
        }
        .conflict-modal-content {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          padding: 30px;
          border-radius: 12px;
          text-align: center;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          font-family: 'Montserrat', sans-serif;
        }
        .conflict-icon {
          font-size: 50px;
          margin-bottom: 15px;
        }
        .conflict-modal-content h3 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 20px;
          font-family: 'Montserrat', sans-serif;
        }
        .conflict-modal-content p {
          margin: 0 0 20px 0;
          color: #666;
          line-height: 1.5;
          font-family: 'Montserrat', sans-serif;
        }
        .conflict-reload-btn {
          background: #d4943c;
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: background 0.3s;
          font-family: 'Montserrat', sans-serif;
        }
        .conflict-reload-btn:hover {
          background: #c4842c;
        }
      `;
      document.head.appendChild(style);
    } else {
      document.getElementById("conflictMessage").textContent = message;
    }

    modal.style.display = "block";
  },

  /**
   * Hide conflict warning
   */
  hideConflictWarning() {
    const modal = document.getElementById("reservationConflictModal");
    if (modal) {
      modal.style.display = "none";
    }
  },

  /**
   * Mark reserved-by-others tickets in the grid
   */
  markReservedTickets() {
    const self = this;
    this.tickets.forEach((ticket) => {
      if (ticket.is_reserved_by_other) {
        const ticketDiv = document.querySelector(
          `.ticketAVL[data-ticket-id="${ticket.id}"]`
        );
        if (ticketDiv) {
          ticketDiv.classList.add("reservedByOther");
          const checkbox = ticketDiv.querySelector(".realTicketCheckbox");
          if (checkbox) {
            checkbox.disabled = true;
          }
        }
      }
    });
  },

  /**
   * Pre-select user's reserved tickets in the grid
   */
  preselectReservedTickets() {
    const self = this;
    if (!this.hasExistingReservation || this.reservedTicketIds.length === 0) {
      return;
    }

    console.log("Pre-selecting reserved tickets:", this.reservedTicketIds);

    this.reservedTicketIds.forEach((ticketId) => {
      const checkbox = document.querySelector(
        `.realTicketCheckbox[data-ticket-id="${ticketId}"]`
      );
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        const ticketDiv = checkbox.closest(".ticketAVL");
        if (ticketDiv) {
          ticketDiv.classList.add("selectedTicket");
        }
      }
    });

    // Update the counter and button state
    const selectedCount = this.reservedTicketIds.length;
    const maxSelection =
      this.remainingToClaim ||
      parseInt(document.getElementById("avaliabletc01")?.value) ||
      0;

    const ad1 = document.getElementById("ad1");
    const ad2 = document.getElementById("ad2");
    if (ad1) ad1.innerText = selectedCount;
    if (ad2) ad2.innerText = maxSelection;

    const selectBtn = document.getElementById("selectbtn");
    if (selectBtn && selectedCount === maxSelection) {
      selectBtn.disabled = false;
      selectBtn.classList.add("enableBtn");
      // Disable unchecked checkboxes
      document.querySelectorAll(".realTicketCheckbox").forEach((cb) => {
        if (!cb.checked) {
          cb.disabled = true;
        }
      });
    }
  },
};

// Make it globally available
window.RealLottery = RealLottery;
